import intersection from 'hs-lodash/intersection';
import getLangLocale from 'I18n/utils/getLangLocale';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'quartz-routing/react-router';
import { Elements } from 'react-stripe-js';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import OrderSummary from '../components/OrderSummary/OrderSummary';
import PaymentStatus from '../components/PaymentInfo/PaymentStatus';
import TestModeAlert from '../components/Utilities/TestModeAlert';
import PaymentContainer from './PaymentContainer';
import { Alert } from 'foundations-components/transitional/components/Alert';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ACH, AFFIRM, AFFIRM_METHOD, AUTO, BACS, CARD, ELEMENTS_APPEARANCE, ELEMENTS_FONTS, FC_VERIFICATION_METHOD, KLARNA, KLARNA_METHOD, OFF_SESSION, PADS, PAYMENT, SEPA, SETUP } from 'payments-checkout-lib/constants/PaymentElement';
import { BYO_STRIPE, HS_PAYMENTS } from 'payments-checkout-lib/constants/Processor';
import MeetingTime from '../components/OrderSummary/MeetingTime';
import OrderDescription from '../components/OrderSummary/OrderDescription';
import { CANCELED, OPEN } from '../constants/Statuses';
import useEscapeListener from '../hooks/useEscapeListener';
import { useFireAlarm } from '../hooks/useFireAlarm';
import { useTransactionLimits } from '../hooks/useTransactionLimits';
import { hsToStripePaymentMethod, isBnpl } from 'payments-checkout-lib/utils/paymentMethods';
import { getValidPaymentMethods } from '../utils/paymentMethods';
import { getIframeLayout, LAYOUTS } from '../utils/queryParams';
import { getStripePromise } from 'payments-checkout-lib/utils/stripe';
import { initializeSubscriptionTracker } from '../utils/subscriptionTracking';
import { getPageInteractionSections, getSalesTaxType, initializePaymentsTracker, PAYMENT_SCHEDULE_MARKER, track } from '../utils/tracking';
import { getIsIframePreviewTestUrl, getIsIframePreviewUrl } from '../utils/urls';
import { getIsZeroCheckoutValid, isContractSetupCheckout } from '../utils/validations';
import HubSpotTermsOfServiceFooter from 'payments-checkout-lib/components/HubSpotTermsOfServiceFooter';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import FullPageError from '../components/Utilities/FullPageError';
import { View } from 'foundations-components/components/View';
import { useTheme } from 'foundations-theming/utils';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FIRE_ALARM_CONTAINER_ID = 'fire-alarm-container';
const STATUS_SUBSCREENS = {
  SOURCE_ID_PAID: 'payment-status-paid',
  PENDING: 'payment-status-pending',
  SUBMITTED: 'payment-status-succeeded',
  SUCCEEDED: 'payment-status-succeeded',
  FAILED: 'payment-status-failed',
  OPEN: 'payment-status-open',
  CANCELED: 'payment-status-canceled'
};
const getPaymentMethodTypes = ({
  paymentMethods,
  paymentAmount,
  minimumAmounts,
  maximumAmounts,
  isPartialPaymentsEnabled
}) => {
  if (paymentMethods.length === 1) {
    return [hsToStripePaymentMethod(paymentMethods[0])];
  }
  if (isPartialPaymentsEnabled) {
    return paymentMethods.map(hsToStripePaymentMethod);
  }
  const validPaymentMethods = maximumAmounts && getValidPaymentMethods(paymentMethods, paymentAmount, maximumAmounts, minimumAmounts);
  return validPaymentMethods;
};
export const getStripeOptions = (checkoutSession, paymentMethods, isZeroCheckout, theme, minimumAmounts, maximumAmounts, isPartialPaymentsEnabled, selectedPaymentMethod, isSubmitPending) => {
  const processorType = checkoutSession.processorType;
  const connectedAccountId = checkoutSession.connectedAccountId;
  const subtotalsPublicResponse = checkoutSession.subtotalsPublicResponse;
  const collectPaymentMethodOnFile = checkoutSession.collectPaymentMethodOnFile;
  const recurringSubtotals = subtotalsPublicResponse.recurringSubtotals;
  const paymentAmountInSmallestCurrencyUnit = subtotalsPublicResponse.paymentAmountInSmallestCurrencyUnit;
  const paymentAmount = subtotalsPublicResponse.paymentAmount;
  const hasRecurringSubtotals = recurringSubtotals && recurringSubtotals.length > 0;
  const getAmount = () => {
    const maxAmount = paymentMethods.length > 0 && !!maximumAmounts ? Math.max(...paymentMethods.filter(method => maximumAmounts[method] !== undefined).map(method => maximumAmounts[method])) : null;
    const minAmount = paymentMethods.length > 0 && !!minimumAmounts ? Math.min(...paymentMethods.map(method => {
      var _minimumAmounts$metho;
      return (_minimumAmounts$metho = minimumAmounts[method]) !== null && _minimumAmounts$metho !== void 0 ? _minimumAmounts$metho : 0;
    })) : null;
    const isPaymentAmountLessThanOrEqualToMaxAmount = maxAmount && paymentAmount <= maxAmount;
    const isPaymentAmountGreaterThanOrEqualToMinimumAmount = minimumAmounts && paymentAmount >= (minAmount !== null && minAmount !== void 0 ? minAmount : 0);
    if (isPaymentAmountGreaterThanOrEqualToMinimumAmount && isPaymentAmountLessThanOrEqualToMaxAmount) {
      return paymentAmountInSmallestCurrencyUnit;
    } else {
      return null;
    }
  };
  const hasSelectedBnpl = selectedPaymentMethod && isBnpl(selectedPaymentMethod);
  const pmofApplicable = hasRecurringSubtotals || collectPaymentMethodOnFile;
  const mode = isZeroCheckout || isContractSetupCheckout(checkoutSession) ? SETUP : PAYMENT;
  const setup_future_usage = pmofApplicable && !hasSelectedBnpl ? OFF_SESSION : null;
  return {
    loader: AUTO,
    locale: getLangLocale(),
    onBehalfOf: processorType === HS_PAYMENTS ? connectedAccountId : null,
    mode,
    setup_future_usage: isSubmitPending && mode === PAYMENT ? setup_future_usage : null,
    amount: mode === PAYMENT ? getAmount() : null,
    currency: checkoutSession.currencyCode.toLowerCase(),
    paymentMethodCreation: 'manual',
    paymentMethodTypes: getPaymentMethodTypes({
      paymentMethods: mode === SETUP || isSubmitPending && selectedPaymentMethod && !isBnpl(selectedPaymentMethod) ? paymentMethods.filter(method => !isBnpl(method)) : paymentMethods,
      paymentAmount,
      // Skip minimum amount filtering when paymentAmount is 0 — applying minimums at $0 always
      // eliminates every payment method.
      minimumAmounts: paymentAmount === 0 ? undefined : minimumAmounts,
      maximumAmounts,
      isPartialPaymentsEnabled
    }),
    paymentMethodOptions: Object.assign({
      us_bank_account: {
        verification_method: FC_VERIFICATION_METHOD
      }
    }, mode === PAYMENT ? {
      [AFFIRM_METHOD]: {
        setup_future_usage: null
      },
      [KLARNA_METHOD]: {
        setup_future_usage: null
      }
    } : {}),
    fonts: ELEMENTS_FONTS,
    appearance: ELEMENTS_APPEARANCE(theme)
  };
};
const CheckoutContainer = ({
  checkoutSession,
  trackPageView
}) => {
  var _checkoutSession$part;
  const {
    pathname
  } = useLocation();
  const {
    formState
  } = usePaymentFormContext();
  const theme = useTheme();
  const paymentMethods = checkoutSession.paymentMethods;
  const orderedPaymentMethods = useMemo(() => {
    const acceptedPaymentMethods = paymentMethods.map(({
      paymentMethod
    }) => paymentMethod);
    // intersection to order acceptedPaymentMethods consistently
    // Order per spec: Card > ACH > BACS > PADS > SEPA > Affirm > Klarna > (wallets handled by Stripe)
    return intersection([CARD, ACH, BACS, PADS, SEPA, AFFIRM, KLARNA], acceptedPaymentMethods);
  }, [paymentMethods]);
  const {
    limits,
    loading: loadingTransactionLimits
  } = useTransactionLimits(checkoutSession.portalId, checkoutSession.currencyCode);
  const minimumAmounts = limits && limits.minimumAmountForPaymentMethod;
  const maximumAmounts = limits && limits.maximumAmountForPaymentMethod;
  const isIframePreviewUrl = getIsIframePreviewUrl(pathname);
  const isIframePreviewTestUrl = getIsIframePreviewTestUrl(pathname);
  const iFrameLayout = getIframeLayout();
  const isCompactEmbed = iFrameLayout === LAYOUTS.EMBED;
  const isFullEmbed = iFrameLayout === LAYOUTS.EMBED_FULL;
  const publishableApiKey = checkoutSession.publishableApiKey;
  const connectedAccountId = checkoutSession.connectedAccountId;
  const processorType = checkoutSession.processorType;
  const isBYOSPortal = processorType === BYO_STRIPE;
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse.paymentAmount, checkoutSession.lineItems);
  const status = checkoutSession.status;
  const isLiveMode = checkoutSession.liveMode;
  const isPreview = isIframePreviewUrl || isIframePreviewTestUrl;
  const shouldShowTestModeAlert = !isLiveMode && (!isIframePreviewUrl || isIframePreviewTestUrl) && status === OPEN;
  const shouldShowPastDueInvoicesBanner = !!checkoutSession.invoiceCheckoutInfo && !!checkoutSession.hasPastDueInvoicesForContract && status === OPEN;
  useEscapeListener(checkoutSession.sourceId);
  useEffect(() => {
    var _checkoutSession$user;
    const companyName = (_checkoutSession$user = checkoutSession.userDefinedBranding) === null || _checkoutSession$user === void 0 ? void 0 : _checkoutSession$user.companyName;
    if (companyName) {
      document.title = unescapedText('payments.checkoutTitle', {
        companyName
      });
    } else {
      document.title = unescapedText('payments.checkoutHeader');
    }
    initializeSubscriptionTracker(checkoutSession.portalId);
    const sourceUrl = checkoutSession.sourceUrl;
    const supportEmail = checkoutSession.companyDetails.supportEmail;
    initializePaymentsTracker({
      portalId: checkoutSession.portalId,
      sourceUrl,
      workflow: 'checkout',
      merchantSupportEmail: supportEmail
    });
    if (trackPageView && status in STATUS_SUBSCREENS) {
      const numLineItemsOpenAmount = checkoutSession.lineItems.filter(lineItem => {
        var _lineItem$customConfi;
        return !!((_lineItem$customConfi = lineItem.customConfiguration) !== null && _lineItem$customConfi !== void 0 && _lineItem$customConfi.hasEditablePrice);
      }).length;
      const numLineItemsWithTax = checkoutSession.lineItems.filter(lineItem => !!lineItem.tax).length;
      const paymentScheduleEnabled = checkoutSession.lineItems.some(lineItem => {
        const sourceId = lineItem.sourceId;
        return !!sourceId && sourceId.includes(PAYMENT_SCHEDULE_MARKER);
      });
      track('Pageview', {
        subscreen: STATUS_SUBSCREENS[status],
        paymentSessionId: checkoutSession.id,
        interactionSections: getPageInteractionSections(checkoutSession),
        numContactFormFields: checkoutSession.formFields.length,
        numLineItemsEditable: checkoutSession.lineItems.filter(lineItem => {
          var _lineItem$customConfi2;
          return !!((_lineItem$customConfi2 = lineItem.customConfiguration) !== null && _lineItem$customConfi2 !== void 0 && _lineItem$customConfi2.hasEditableQuantity);
        }).length,
        paymentScheduleEnabled,
        requiredCheckout: !checkoutSession.lineItems.some(lineItem => {
          var _lineItem$customConfi3;
          return !!((_lineItem$customConfi3 = lineItem.customConfiguration) !== null && _lineItem$customConfi3 !== void 0 && _lineItem$customConfi3.isOptional);
        }),
        numLineItemsOpenAmount,
        numLineItemsWithTax,
        dealPipelineStageId: numLineItemsOpenAmount,
        discountShown: !!checkoutSession.isDiscountCodeEnabled,
        sourceApp: checkoutSession.sourceApp,
        paymentType: checkoutSession.processorType,
        isPaymentMethodOnFileEnabled: checkoutSession.collectPaymentMethodOnFile,
        salesTax: getSalesTaxType(checkoutSession)
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mounts a fire alarm for this app if one is active
  useFireAlarm(FIRE_ALARM_CONTAINER_ID);
  const isPartialPaymentsEnabled = (_checkoutSession$part = checkoutSession.partialPaymentsInfo) === null || _checkoutSession$part === void 0 ? void 0 : _checkoutSession$part.isPartialPaymentsEnabled;
  const selectedPaymentMethod = formState.paymentMethod;
  const isCheckoutSubmitPending = formState.isCheckoutSubmitPending;
  const stripeOptions = useMemo(() => getStripeOptions(checkoutSession, orderedPaymentMethods, isZeroCheckout, theme, minimumAmounts, maximumAmounts, isPartialPaymentsEnabled, selectedPaymentMethod, isCheckoutSubmitPending),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [orderedPaymentMethods, minimumAmounts, maximumAmounts, isPartialPaymentsEnabled, isZeroCheckout, selectedPaymentMethod, isCheckoutSubmitPending, checkoutSession.subtotalsPublicResponse]);
  if (loadingTransactionLimits) return null;
  if (status === CANCELED) {
    return /*#__PURE__*/_jsx(PaymentStatus, {
      checkoutSession: checkoutSession
    });
  }
  if (formState.pageError) {
    return /*#__PURE__*/_jsx(FullPageError, {});
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      id: FIRE_ALARM_CONTAINER_ID,
      marginBlockEnd: "400",
      as: "div"
    }), shouldShowTestModeAlert && /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "600",
      marginBlockStart: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(TestModeAlert, {
        checkoutSession: checkoutSession
      })
    }), shouldShowPastDueInvoicesBanner && /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "600",
      marginBlockStart: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(Alert, {
        "data-test-id": "past-due-invoices-banner",
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.pastDueInvoicesBanner.title"
        }),
        type: "danger",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "payments.pastDueInvoicesBanner.body"
        })
      })
    }), /*#__PURE__*/_jsx(Elements, {
      stripe: getStripePromise(publishableApiKey, isBYOSPortal, isPreview ? null : connectedAccountId),
      options: stripeOptions,
      children: /*#__PURE__*/_jsxs(Grid, {
        "data-test-id": "checkout-container",
        children: [!isCompactEmbed && /*#__PURE__*/_jsxs(GridItem, {
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: isFullEmbed ? 12 : 6
          },
          children: [/*#__PURE__*/_jsx(OrderSummary, {
            checkoutSession: checkoutSession
          }), status === OPEN && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(MeetingTime, {}), /*#__PURE__*/_jsx(OrderDescription, {
              checkoutSession: checkoutSession
            })]
          })]
        }), /*#__PURE__*/_jsx(GridItem, {
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: isFullEmbed || isCompactEmbed ? 12 : 6
          },
          children: status === OPEN ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(PaymentContainer, {
              checkoutSession: checkoutSession
            }), /*#__PURE__*/_jsx(HubSpotTermsOfServiceFooter, {})]
          }) : /*#__PURE__*/_jsx(PaymentStatus, {
            checkoutSession: checkoutSession
          })
        })]
      })
    })]
  });
};
export default CheckoutContainer;